import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import {
  AccessTime as ClockIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import * as PATHS from 'js/constants/pathnames';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import { Select } from 'new/elements/Select';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { showAlert } from 'new/stores/showAlert/action';
import { useFetchAdminOrgTiers, useUpdateAdminOrg } from 'new/stores/adminOrgs/adminOrgs.api';
import { AdminOrg } from 'new/stores/adminOrgs/adminOrgs.types';
import { AdminRole } from '../../RoleBanner';
import { GuardedControl } from '../../GuardedControl';
import { renderPlanMenuItem } from '../../PlanMenuItem';
import { buildPlanOptions, daysUntil, PlanOption } from '../../utils';
import tabStyles from './tabs.module.scss';

interface PlanLimitsTabProps {
  org: AdminOrg;
  role: AdminRole;
}

const EXTEND_PRESETS = [7, 14, 30, 60];

const NOT_SELF_SIGNUP_TOOLTIP =
  "Trial extension requires self-signup status, which is set only by Nexla's own self-serve " +
  "signup flow — it can't be granted through this tool (or any API) for any org, including ones " +
  'created here. The backend rejects trial date changes for non-self-signup orgs outright.';

export function PlanLimitsTab({ org, role }: PlanLimitsTabProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { data: tiers = [] } = useFetchAdminOrgTiers();
  const [updateOrg, { isLoading: isChangingTier }] = useUpdateAdminOrg();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const tier = org.org_tier;
  const disabled = role !== 'full';

  const [pendingTierName, setPendingTierName] = useState(tier?.name ?? '');
  useEffect(() => {
    setPendingTierName(tier?.name ?? '');
  }, [tier?.id]);

  const hasPendingTierChange = pendingTierName !== (tier?.name ?? '') && !!pendingTierName;
  const pendingTier = tiers.find((t) => t.name === pendingTierName);

  const planItems = useMemo(() => buildPlanOptions(tiers), [tiers]);

  const onApplyTier = () => {
    if (!hasPendingTierChange) return;
    updateOrg({ id: org.id, body: { org_tier: pendingTierName } }).then((result) => {
      if ('error' in result) {
        dispatch(showAlert({ title: 'Failed to change plan', severity: 'error' }));
        return;
      }
      dispatch(
        showAlert({ title: `Plan switched to ${pendingTier?.display_name}`, severity: 'success' }),
      );
    });
  };

  const onEditPlanTiers = () => {
    if (!tier) return;
    history.push(`${PATHS.ROUTE_REDESIGN}/settings/plan-tiers?tierId=${tier.id}`);
  };

  const trialDisabled = disabled || !org.self_signup;
  const trialDays = daysUntil(org.trial_expires_at);

  const extendBy = (days: number) => {
    const base = org.trial_expires_at ? new Date(org.trial_expires_at) : new Date();
    const next = new Date(Math.max(base.getTime(), Date.now()));
    next.setDate(next.getDate() + days);
    updateOrg({ id: org.id, body: { trial_expires_at: next.toISOString() } }).then((result) => {
      if ('error' in result) {
        dispatch(showAlert({ title: 'Failed to extend trial', severity: 'error' }));
        return;
      }
      dispatch(showAlert({ title: `Trial extended by ${days} days`, severity: 'success' }));
    });
  };

  const onEndTrialNow = () => {
    confirm({
      title: 'End trial now',
      message: `This immediately ends the trial for "${org.name}". Depending on their tier, this may restrict their access right away.`,
      confirmText: 'End trial',
      variant: 'danger',
      onConfirm: () =>
        updateOrg({ id: org.id, body: { trial_expires_at: null } }).then((result) => {
          if ('error' in result) {
            dispatch(showAlert({ title: 'Failed to end trial', severity: 'error' }));
            return;
          }
          dispatch(showAlert({ title: 'Trial ended', severity: 'success' }));
        }),
    });
  };

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="subtitle2" gutterBottom>
          Plan
        </Typography>
        <Typography variant="body3" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Current plan: <strong>{tier?.display_name ?? '—'}</strong>
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <GuardedControl disabled={disabled || isChangingTier}>
            <Select
              containerSx={{ width: 300 }}
              displayEmpty
              value={pendingTierName}
              items={planItems}
              onChange={(event) => setPendingTierName(event.target.value as string)}
              renderValue={(value) =>
                tiers.find((t) => t.name === value)?.display_name ?? 'No plan'
              }
              renderItem={(item) => renderPlanMenuItem(item as PlanOption)}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: { style: { maxHeight: 300, width: 300 } },
              }}
            />
          </GuardedControl>
          <GuardedControl disabled={disabled || isChangingTier || !hasPendingTierChange}>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              disabled={isChangingTier}
              onClick={onApplyTier}
            >
              Apply
            </Button>
          </GuardedControl>
        </Stack>
        {hasPendingTierChange && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="body3" color="warning.main" display="block">
              Switching to <strong>{pendingTier?.display_name}</strong> — click Apply to confirm.
            </Typography>
            {org.status === 'DEACTIVATED' && (
              <Typography variant="body3" color="warning.main" display="block">
                Warning: this org is currently suspended. Changing the plan will automatically
                reactivate it.
              </Typography>
            )}
          </Stack>
        )}
      </div>

      <div>
        {!org.self_signup && org.trial_expires_at && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This date is informational only, not enforced — it was auto-applied from the tier's
            trial period at creation, but Nexla's nightly trial-expiry job only acts on self-signup
            orgs. It won't pause flows or restrict access on its own, and it can't be changed here
            since editing requires self-signup status, which is granted only through Nexla's
            self-serve signup flow.
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <ClockIcon color={org.trial_expires_at ? 'warning' : 'disabled'} />
              <div>
                <Typography variant="subtitle1">
                  {org.trial_expires_at
                    ? `Trial ends ${new Date(org.trial_expires_at).toLocaleDateString()}`
                    : 'Account is not time-limited'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {org.trial_expires_at
                    ? trialDays >= 0
                      ? `${trialDays} day(s) remaining`
                      : `Expired ${Math.abs(trialDays)} day(s) ago`
                    : 'No trial expiration date is on file for this org.'}
                </Typography>
              </div>
            </Stack>
          </CardContent>
        </Card>

        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            Extend trial
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <div className={tabStyles.pickerGrid} style={{ flex: 1 }}>
              {EXTEND_PRESETS.map((days) => (
                <GuardedControl
                  key={days}
                  disabled={trialDisabled || isChangingTier}
                  tooltip={!org.self_signup ? NOT_SELF_SIGNUP_TOOLTIP : undefined}
                >
                  <Button variant="outlined" onClick={() => extendBy(days)}>
                    Extend {days} days
                  </Button>
                </GuardedControl>
              ))}
            </div>
            <GuardedControl
              disabled={trialDisabled || isChangingTier || !org.trial_expires_at}
              tooltip={!org.self_signup ? NOT_SELF_SIGNUP_TOOLTIP : undefined}
            >
              <Button
                variant="contained"
                color="error"
                startIcon={<CloseIcon />}
                onClick={onEndTrialNow}
              >
                End trial now
              </Button>
            </GuardedControl>
          </Stack>
        </div>
      </div>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Tier limits — {tier?.display_name ?? '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} display="block">
            Seats, source count, and records limits are defined on the tier itself, not per org —
            editing them here would silently change every org on{' '}
            <strong>{tier?.display_name}</strong>. Manage those in Plan Tiers instead.
          </Typography>
          <GuardedControl
            disabled={!tier}
            tooltip={!tier ? 'Assign a plan to this org first' : undefined}
          >
            <Button variant="outlined" endIcon={<ArrowForwardIcon />} onClick={onEditPlanTiers}>
              Edit {tier?.display_name ?? 'plan'} tier
            </Button>
          </GuardedControl>
        </CardContent>
      </Card>

      {ConfirmDialogComponent}
    </Stack>
  );
}
